import { useRef, useState } from 'react';
import { Pencil } from 'lucide-react';

import {
  useUpdateStorefrontSettings,
  type AdminStorefrontDetail,
  type StorefrontSettingsInput,
} from '@/lib/api/storefrontAdminHooks';

import { hasSystemToken, isReservedSlug } from './storeCompliance';
import {
  STORE_ROOT_DOMAIN,
  canonicalMedicalGroup,
  isRealMedicalGroup,
  refusalMessage,
} from './storefrontFormat';
import { ActionButton, Field, Notice, Panel, inputClass } from './ui';

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,39}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Store settings an admin may change at any time (`PATCH
 * /admin/storefronts/{id}`): name, slug (the store's address), support
 * email and the reviewing medical group. Owners cannot change the medical
 * group; a reserved slug or a SYSTEM-wordmark name from before those rules
 * is fixed here.
 */
export function SettingsPanel({ store }: { store: AdminStorefrontDetail }) {
  const [editing, setEditing] = useState(false);
  return (
    <Panel
      title="Store settings"
      aside={
        !editing && (
          <ActionButton tone="ghost" className="h-30 px-10" onClick={() => setEditing(true)}>
            <Pencil size={12} aria-hidden /> Edit
          </ActionButton>
        )
      }
    >
      {editing ? (
        <SettingsForm store={store} onDone={() => setEditing(false)} />
      ) : (
        <dl className="grid grid-cols-1 gap-14">
          <Field label="Name">{store.name}</Field>
          <Field label="Address">
            <span className="font-mono text-12">
              {store.slug}.{STORE_ROOT_DOMAIN}
            </span>
          </Field>
          <Field label="Support email">{store.support_email || '—'}</Field>
          <Field label="Medical group">
            {isRealMedicalGroup(store.medical_group_name, store.medical_group_options) ? (
              store.medical_group_name
            ) : store.medical_group_name ? (
              <span className="text-warning">
                {store.medical_group_name}: not one of the groups we work with (required to go
                live)
              </span>
            ) : (
              <span className="text-warning">Not set (required to go live)</span>
            )}
          </Field>
        </dl>
      )}
    </Panel>
  );
}

function SettingsForm({ store, onDone }: { store: AdminStorefrontDetail; onDone: () => void }) {
  const update = useUpdateStorefrontSettings(store.id);
  const [name, setName] = useState(store.name);
  const [slug, setSlug] = useState(store.slug);
  const [email, setEmail] = useState(store.support_email ?? '');
  const options = store.medical_group_options ?? [];
  const [group, setGroup] = useState(
    isRealMedicalGroup(store.medical_group_name, options)
      ? (options.length
          ? (canonicalMedicalGroup(store.medical_group_name, options) ?? '')
          : (store.medical_group_name ?? ''))
      : '',
  );
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const changes: StorefrontSettingsInput = {};
  if (name.trim() !== store.name) changes.name = name.trim();
  if (slug.trim() !== store.slug) changes.slug = slug.trim();
  if (email.trim() !== (store.support_email ?? '')) changes.support_email = email.trim();
  if (group.trim() !== (store.medical_group_name ?? '').trim() && group.trim()) {
    changes.medical_group_name = group.trim();
  }

  const slugError =
    'slug' in changes
      ? !SLUG_RE.test(changes.slug ?? '')
        ? 'Use 2 to 40 lowercase letters, digits or hyphens, starting with a letter or digit.'
        : isReservedSlug(changes.slug)
          ? 'That address is reserved.'
          : hasSystemToken(changes.slug)
            ? 'Store addresses may not use the SYSTEM wordmark.'
            : null
      : null;
  const nameError = !name.trim()
    ? 'Enter a store name.'
    : 'name' in changes && hasSystemToken(changes.name)
      ? 'Store names may not use the SYSTEM wordmark.'
      : null;
  const emailError = !EMAIL_RE.test(email.trim()) ? 'Enter a valid support email.' : null;
  const groupError =
    group.trim() && !isRealMedicalGroup(group, options)
      ? options.length
        ? 'Choose one of the groups we work with.'
        : 'Replace the [bracketed] placeholder with the group’s real name.'
      : null;
  const groupMissingLive =
    store.status === 'live' &&
    !group.trim() &&
    !isRealMedicalGroup(store.medical_group_name, options);
  const hasChanges = Object.keys(changes).length > 0;
  const valid = !slugError && !nameError && !emailError && !groupError;

  return (
    <form
      className="flex flex-col gap-14"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!hasChanges || !valid || inFlight.current) return;
        inFlight.current = true;
        setError(null);
        try {
          await update.mutateAsync(changes);
          onDone();
        } catch (err) {
          setError(refusalMessage(err));
        } finally {
          inFlight.current = false;
        }
      }}
    >
      <Input label="Name" value={name} onChange={setName} error={nameError} maxLength={80} />
      <Input
        label="Address (slug)"
        value={slug}
        onChange={(v) => setSlug(v.toLowerCase())}
        error={slugError}
        maxLength={40}
        suffix={`.${STORE_ROOT_DOMAIN}`}
        mono
      />
      {'slug' in changes && !slugError && store.status === 'live' && (
        <Notice tone="warning">
          The live store moves to {changes.slug}.{STORE_ROOT_DOMAIN}. Links to the old address stop
          working, and open preview links expire.
        </Notice>
      )}
      <Input
        label="Support email"
        value={email}
        onChange={setEmail}
        error={emailError}
        maxLength={254}
        type="email"
      />
      {options.length > 0 ? (
        <label className="flex flex-col gap-6">
          <span className="type-label-xs text-ink-muted">Medical group</span>
          <select className={inputClass} value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="" disabled>
              Choose the group whose providers review this store
            </option>
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <span className="type-body-sm text-ink-muted leading-130">
            The groups we work with (backend setting). Shown to patients in the store&apos;s
            disclaimer.
          </span>
        </label>
      ) : (
        <Input
          label="Medical group"
          value={group}
          onChange={setGroup}
          error={groupError}
          maxLength={120}
          placeholder="The group whose providers review this store"
        />
      )}
      {groupMissingLive && (
        <Notice tone="warning">
          This store is live without a real medical group; its disclaimer shows a placeholder to
          patients. Enter the group.
        </Notice>
      )}
      {error && <Notice tone="danger">{error}</Notice>}
      <div className="flex items-center justify-end gap-8">
        <ActionButton tone="ghost" onClick={onDone} disabled={update.isPending}>
          Cancel
        </ActionButton>
        <ActionButton type="submit" tone="primary" disabled={!hasChanges || !valid || update.isPending}>
          {update.isPending ? 'Saving…' : 'Save changes'}
        </ActionButton>
      </div>
    </form>
  );
}

function Input({
  label,
  value,
  onChange,
  error,
  maxLength,
  type = 'text',
  placeholder,
  suffix,
  mono = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error: string | null;
  maxLength: number;
  type?: string;
  placeholder?: string;
  suffix?: string;
  mono?: boolean;
}) {
  return (
    <label className="flex flex-col gap-6">
      <span className="type-label-xs text-ink-muted">{label}</span>
      <span className="flex items-stretch">
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={maxLength}
          placeholder={placeholder}
          aria-invalid={Boolean(error)}
          className={`${inputClass} ${mono ? 'font-mono' : ''} ${error ? 'border-[#B42318]' : ''}`}
        />
        {suffix && (
          <span className="h-36 px-8 inline-flex items-center border border-l-0 border-line bg-sunken font-mono text-11 text-ink-muted whitespace-nowrap">
            {suffix}
          </span>
        )}
      </span>
      {error && <span className="type-body-sm text-[#B42318]">{error}</span>}
    </label>
  );
}
